import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const QuillEditor = forwardRef(function QuillEditor(
  {
    placeholder = 'Ghi chú nếu có',
    isReadOnly = false,
    sx = {height: 100},
    value = '',
    onChange,
  },
  ref
) {
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const latestContent = useRef(value || '');
  
  useImperativeHandle(ref, () => ({
    getValue: () => latestContent.current,
  
    setValue: (newValue) => {
      if (quillInstance.current) {
        const content = newValue || '';
        quillInstance.current.root.innerHTML = content;
        latestContent.current = content;
      }
    },
  
    reset: () => {
      if (quillInstance.current) {
        quillInstance.current.root.innerHTML = '';
        latestContent.current = '';
      }
    },
  
    refresh: () => {
      if (quillInstance.current) {
        const html = quillInstance.current.root.innerHTML;
        quillInstance.current.root.innerHTML = html; // Force a redraw by reassigning
      }
    },
  }));
  

  // Initialize Quill only once
  useEffect(() => {
    if (quillRef.current && !quillInstance.current) {
      const effectivePlaceholder = isReadOnly || value ? '' : placeholder;
      quillInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: isReadOnly
            ? []
            : [
                // Full toolbar options
                // [{ 'header': [1, 2, 3, false] }],
                // [{ 'font': [] }],
                // [{ 'size': ['small', false, 'large', 'huge'] }],
                ['bold', 'italic', 'underline', 'strike'],
                [
                  { color: [] },
                  { background: ['#f44336', '#ffeb3b', '#4caf50', null] },
                ], // Transparent option with 'null'
                // [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                // [{ 'indent': '-1' }, { 'indent': '+1' }],
                // [{ align: [] }],
                // ['link', 'image', 'video'],
                // ['clean'],
              ],
        },
        placeholder: effectivePlaceholder,
        readOnly: isReadOnly,
      });

      // Set initial content if provided
      if (value) {
        // Remove first and last single quotes if any
        const initialContent = value.replace(/^'|'$/g, '');
        quillInstance.current.root.innerHTML = initialContent;
        latestContent.current = initialContent;
      }

      // Listen for text changes
      quillInstance.current.on('text-change', () => {
        const content = quillInstance.current.root.innerHTML;
        latestContent.current = content;
        onChange?.(content);
      });
    }
  }, []);

  // Update editor content when value prop changes
  useEffect(() => {
    if (
      quillInstance.current &&
      value !== quillInstance.current.root.innerHTML
    ) {
      quillInstance.current.root.innerHTML = value;
    }
  }, [value]);

  // Update editor settings if placeholder or readOnly change
  useEffect(() => {
    if (quillInstance.current) {
      const effectivePlaceholder = isReadOnly || value ? '' : placeholder;
      quillInstance.current.root.setAttribute(
        'data-placeholder',
        effectivePlaceholder
      );
      quillInstance.current.enable(!isReadOnly);
    }
  }, [placeholder, isReadOnly]);

  return (
    <>
      <style>{`
        .editor-wrapper {
          border-top: 1px solid #e0e0e0;
          overflow: hidden;
        }
        .custom-toolbar {
          /* toolbar styles */
        }
        .editor-content {
          min-height: 80px;
          padding: 0px;
          margin: 0;
        }
        .ql-container {
          border: none;
        }
        .ql-editor {
          padding: 8px;
        }
        .hide-toolbar .ql-toolbar {
          display: none;
        }
      `}</style>

      <div className={`editor-wrapper ${isReadOnly ? 'hide-toolbar' : ''}`}>
        <div id="toolbar" className="custom-toolbar ql-toolbar"></div>
        <div
          ref={quillRef}
          className="editor-content"
          style={{ minHeight: sx.height, ...sx }}
        />
      </div>
    </>
  );
});

export default QuillEditor;
